import React, { useState, useRef } from 'react';
import BinLabelPreview from './BinLabelPreview';
import BinLabelPrint from './BinLabelPrint';
import './BinLabelPrinter.css';

const LABEL_TYPES = {
  floor: { name: 'Floor (10x8 Landscape)', width: '10in', height: '8in' },
  shelf: { name: 'Shelf (1x3)', width: '3in', height: '1in' },
  pallet: { name: 'Rack (4x6)', width: '6in', height: '4in' }
};

function BinLabelPrinter() {
  const [labelType, setLabelType] = useState('pallet');
  const [rowType, setRowType] = useState('letter'); // 'letter' or 'number'
  const [row, setRow] = useState('A');
  const [startBay, setStartBay] = useState(1);
  const [numBays, setNumBays] = useState(1);
  const [shelvesPerBay, setShelvesPerBay] = useState(0); // 0 = no shelves (pallet/floor)
  const [generatedBins, setGeneratedBins] = useState([]);
  const [previewBin, setPreviewBin] = useState(null);
  const printRef = useRef();

  const generateBinCodes = () => {
    const bins = [];
    const rowValue = rowType === 'letter' ? row.toUpperCase() : row;

    for (let bay = startBay; bay < startBay + numBays; bay++) {
      if (shelvesPerBay === 0) {
        // No shelves - just row-bay (e.g., A1, A2, B3)
        const binCode = `${rowValue}${bay}`;
        bins.push({
          bin_code: binCode,
          bin_type: labelType,
          row: rowValue,
          bay: bay.toString(),
          shelf: null,
          set_sku: ''
        });
      } else {
        // With shelves - row-bay-shelf (e.g., A-1-1, A-1-2, B-3-4)
        for (let shelf = 1; shelf <= shelvesPerBay; shelf++) {
          const binCode = `${rowValue}-${bay}-${shelf}`;
          bins.push({
            bin_code: binCode,
            bin_type: labelType,
            row: rowValue,
            bay: bay.toString(),
            shelf: shelf.toString()
          });
        }
      }
    }

    setGeneratedBins(bins);
    if (bins.length > 0) {
      setPreviewBin(bins[0]);
    }
  };

  const updateBinSku = (index, sku) => {
    const updatedBins = [...generatedBins];
    updatedBins[index] = { ...updatedBins[index], set_sku: sku.toUpperCase() };
    setGeneratedBins(updatedBins);
    if (previewBin?.bin_code === updatedBins[index].bin_code) {
      setPreviewBin(updatedBins[index]);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleRowChange = (value) => {
    if (rowType === 'letter') {
      setRow(value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2));
    } else {
      setRow(value.replace(/[^0-9]/g, ''));
    }
  };

  return (
    <div className="bin-label-printer">
      <header className="printer-header">
        <h1>Bin Label Printer</h1>
      </header>

      <main className="printer-content">
        <div className="control-panel">
          <div className="panel-section">
            <h3>Generate Bin Range</h3>

            <div className="form-row">
              <label>Label Size</label>
              <select
                value={labelType}
                onChange={(e) => setLabelType(e.target.value)}
                className="form-control"
              >
                {Object.entries(LABEL_TYPES).map(([key, val]) => (
                  <option key={key} value={key}>{val.name}</option>
                ))}
              </select>
            </div>

            <div className="form-row">
              <label>Row Type</label>
              <div className="toggle-group">
                <button
                  type="button"
                  className={`toggle-btn ${rowType === 'letter' ? 'active' : ''}`}
                  onClick={() => { setRowType('letter'); setRow('A'); }}
                >
                  Letter (A, B, C)
                </button>
                <button
                  type="button"
                  className={`toggle-btn ${rowType === 'number' ? 'active' : ''}`}
                  onClick={() => { setRowType('number'); setRow('1'); }}
                >
                  Number (1, 2, 3)
                </button>
              </div>
            </div>

            <div className="form-row">
              <label>Row</label>
              <input
                type="text"
                value={row}
                onChange={(e) => handleRowChange(e.target.value)}
                placeholder={rowType === 'letter' ? 'A' : '1'}
                className="form-control row-input"
              />
            </div>

            <div className="form-row-group">
              <div className="form-row half">
                <label>Starting Bay</label>
                <input
                  type="number"
                  value={startBay}
                  onChange={(e) => setStartBay(Math.max(1, parseInt(e.target.value) || 1))}
                  min="1"
                  className="form-control"
                />
              </div>
              <div className="form-row half">
                <label>Number of Bays</label>
                <input
                  type="number"
                  value={numBays}
                  onChange={(e) => setNumBays(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
                  min="1"
                  max="100"
                  className="form-control"
                />
              </div>
            </div>

            <div className="form-row">
              <label>Shelves per Bay</label>
              <input
                type="number"
                value={shelvesPerBay}
                onChange={(e) => setShelvesPerBay(Math.max(0, Math.min(20, parseInt(e.target.value) || 0)))}
                min="0"
                max="20"
                className="form-control"
              />
              <small className="form-help">0 = No shelves (pallet/floor bins)</small>
            </div>

            <button onClick={generateBinCodes} className="generate-btn">
              Generate Labels
            </button>
          </div>

          {generatedBins.length > 0 && (
            <div className="panel-section">
              <h3>Generated Bins ({generatedBins.length})</h3>
              {labelType === 'floor' && (
                <p className="bin-list-hint">Enter Set SKU for each bin (optional)</p>
              )}
              <div className="bin-list">
                {generatedBins.map((bin, idx) => (
                  <div
                    key={idx}
                    className={`bin-item ${labelType === 'floor' ? 'bin-item-floor' : ''} ${previewBin?.bin_code === bin.bin_code ? 'selected' : ''}`}
                    onClick={() => setPreviewBin(bin)}
                  >
                    <span className="bin-code">{bin.bin_code}</span>
                    {labelType === 'floor' && (
                      <input
                        type="text"
                        value={bin.set_sku || ''}
                        onChange={(e) => {
                          e.stopPropagation();
                          updateBinSku(idx, e.target.value);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        placeholder="Set SKU"
                        className="bin-sku-input"
                      />
                    )}
                  </div>
                ))}
              </div>

              <button onClick={handlePrint} className="print-btn">
                Print {generatedBins.length} Label{generatedBins.length > 1 ? 's' : ''}
              </button>
            </div>
          )}
        </div>

        <div className="preview-panel">
          <h3>Label Preview</h3>
          {previewBin ? (
            <BinLabelPreview bin={previewBin} labelType={labelType} />
          ) : (
            <div className="no-preview">Configure and generate bins to preview</div>
          )}
        </div>
      </main>

      {/* Print-only content */}
      <div className="print-only">
        <BinLabelPrint
          ref={printRef}
          bins={generatedBins}
          labelType={labelType}
        />
      </div>
    </div>
  );
}

export default BinLabelPrinter;
